import { Button, Card } from '@wundo/ui';
import { Mic } from 'lucide-react';
import { m } from '@/paraglide/messages.js';

export interface OfflineRecordingBannerProps {
  /** Whether this browser can record at all. */
  supported: boolean;
  pending: boolean;
  onStart: () => void;
}

/**
 * The offer on a meeting with nowhere for the notetaker to go.
 *
 * A meeting without an online link is the one case where the settings row has
 * nothing useful to say — the notetaker is off because there is no meeting to
 * join, and everything the page normally promises depends on a transcript that
 * will never arrive. So the offer moves out of the panel and onto the page: on
 * these meetings it is not one setting among three, it is the whole of what
 * Wundo can do here.
 *
 * A browser that cannot record says so instead of offering a button that would
 * fail — the same honesty the panel keeps, in the place where it matters more.
 */
export function OfflineRecordingBanner({
  supported,
  pending,
  onStart,
}: OfflineRecordingBannerProps) {
  return (
    <Card as='section' tone='accent' className='mt-4.5'>
      <div className='flex flex-wrap items-center gap-x-5 gap-y-3'>
        <Mic aria-hidden='true' className='size-5 shrink-0 text-ink-secondary' />
        <div className='min-w-0 flex-1'>
          <p className='text-md font-bold text-ink'>{m.meeting_offline_banner_title()}</p>
          <p className='mt-0.5 text-base text-ink-body'>
            {supported
              ? m.meeting_offline_banner_body()
              : m.meeting_offline_unsupported()}
          </p>
        </div>
        {supported && (
          <Button size='sm' disabled={pending} onClick={onStart}>
            {m.meeting_offline_start()}
          </Button>
        )}
      </div>
    </Card>
  );
}
